import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Which page the user just came from.
 *
 * A page that restores remembered state (the Residents grid's selected row and
 * scroll position) has to tell "coming back" from "coming here fresh", and the
 * router alone can't: the sidebar, the dashboard, a Back to list button and a
 * Save all arrive as a plain push or replace. What tells them apart is the
 * page they came from, which is what this records.
 *
 * Module state, not React state: the reading happens in a page's first render
 * (a useState initializer), before any effect of the new location has run.
 */
let committed: string | null = null;
let beforeCommitted: string | null = null;

/** Mount once inside the router. Records each pathname after it renders. */
export function RouteOriginTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname === committed) return;
    beforeCommitted = committed;
    committed = pathname;
  }, [pathname]);
  return null;
}

/**
 * The pathname the user was on before `pathname`, or null on the first page
 * of the session.
 *
 * Two timings are handled. Usually a page's first render happens before the
 * tracker's effect for the new location, so the last recorded path IS the
 * previous page. A lazy route that suspends first lets the tracker record the
 * new location before the page renders, so then it is the one before that.
 */
export function previousPathname(pathname: string): string | null {
  return committed === pathname ? beforeCommitted : committed;
}

/** Test seam: forget the recorded history. */
export function resetRouteOrigin(): void {
  committed = null;
  beforeCommitted = null;
}
